import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  Breadcrumbs,
  DEMENAGEMENT_DATE_MODIFIED,
  DemenagementLayout,
  DictimmoCta,
  InternalLinks,
  SourceList,
  makeBreadcrumbs,
  trackEvent,
} from "./DemenagementCommon";

const path = "/outils/lettre-preavis-location";
const breadcrumbs = makeBreadcrumbs([
  { name: "Déménagement", path: "/demenagement" },
  { name: "Lettre de préavis location", path },
]);

const sources = [
  {
    label: "Service-public : préavis et congé du locataire",
    href: "https://www.service-public.fr/particuliers/vosdroits/F1168",
  },
  {
    label: "Service-public : simulateur/modèle de congé du logement",
    href: "https://www.service-public.fr/simulateur/calcul/CongeLogement",
  },
  {
    label: "Service-public : simulateur de délai de préavis du locataire",
    href: "https://www.service-public.fr/simulateur/calcul/delaiPreavisLocataire",
  },
  {
    label: "ANIL : résiliation du bail",
    href: "https://www.anil.org/parole-expert-logement-location/parole-expert-logement-location-resiliation-bail/",
  },
];

const situationLabels = {
  standard: "Préavis standard",
  zone_tendue: "Logement situé en zone tendue",
  mutation: "Mutation professionnelle",
  perte_emploi: "Perte d'emploi",
  nouvel_emploi: "Nouvel emploi consécutif à une perte d'emploi",
  sante: "État de santé justifiant un changement de domicile",
  rsa_aah: "Bénéficiaire du RSA ou de l'AAH",
  logement_social: "Attribution d'un logement social",
  autre: "Autre motif à vérifier",
};

const modeLabels = {
  lettre_recommandee: "lettre recommandée avec avis de réception",
  remise_main_propre: "remise en main propre contre récépissé ou émargement",
  commissaire_justice: "acte de commissaire de justice",
};

const initialForm = {
  housingType: "vide",
  noticeSituation: "standard",
  tenantName: "",
  tenantAddress: "",
  landlordName: "",
  landlordAddress: "",
  city: "",
  sendDate: new Date().toISOString().slice(0, 10),
  sendMode: "lettre_recommandee",
  requestExitInspection: true,
};

function formatDate(value) {
  if (!value) return "";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getNoticeInfo(form) {
  const reducedSituations = [
    "zone_tendue",
    "mutation",
    "perte_emploi",
    "nouvel_emploi",
    "sante",
    "rsa_aah",
    "logement_social",
  ];

  if (form.housingType === "meublé") {
    return {
      label: "un mois",
      wording: "un préavis d'un mois, sous réserve des règles applicables au bail meublé",
      needsProof: false,
    };
  }

  if (reducedSituations.includes(form.noticeSituation)) {
    return {
      label: "un mois",
      wording: "un préavis réduit à un mois, sous réserve de fournir les justificatifs nécessaires",
      needsProof: true,
    };
  }

  if (form.noticeSituation === "autre") {
    return {
      label: "à vérifier",
      wording: "le délai de préavis applicable à ma situation, que je vous invite à vérifier avec les textes officiels",
      needsProof: true,
    };
  }

  return {
    label: "trois mois",
    wording: "un préavis de trois mois, sauf règle particulière applicable",
    needsProof: false,
  };
}

function buildLetter(form, noticeInfo) {
  const tenantName = form.tenantName || "[Nom du locataire]";
  const tenantAddress = form.tenantAddress || "[Adresse du logement quitté]";
  const landlordName = form.landlordName || "[Nom du bailleur ou de l'agence]";
  const landlordAddress = form.landlordAddress || "[Adresse du bailleur ou de l'agence]";
  const city = form.city || "[Ville]";
  const sendDate = formatDate(form.sendDate) || "[Date]";
  const mode = modeLabels[form.sendMode];
  const situation = situationLabels[form.noticeSituation];

  const inspection = form.requestExitInspection
    ? "Je vous remercie également de bien vouloir me proposer des dates pour organiser l'état des lieux de sortie et la remise des clés."
    : "";

  const proof = noticeInfo.needsProof
    ? "Les justificatifs utiles à l'examen de ma demande sont joints à ce courrier, sous réserve de ceux exigés par les textes applicables."
    : "";

  return {
    html: (
      <article className="move-letter print-document" aria-label="Lettre de préavis générée">
        <p>
          <strong>{tenantName}</strong>
          <br />
          {tenantAddress}
        </p>
        <p className="letter-right">
          {landlordName}
          <br />
          {landlordAddress}
        </p>
        <p className="letter-right">
          Fait à {city}, le {sendDate}
        </p>
        <p>
          Mode d'envoi : {mode}
          <br />
          Situation indiquée : {situation}
        </p>
        <p className="letter-subject">
          Objet : congé du logement situé {tenantAddress}
        </p>
        <p>Madame, Monsieur,</p>
        <p>
          Par la présente, je vous informe de ma décision de donner congé du
          logement que j'occupe à l'adresse suivante : {tenantAddress}.
        </p>
        <p>
          D'après les informations renseignées dans ce modèle, ce congé est donné
          avec {noticeInfo.wording}. Le préavis court à compter de la réception,
          de la remise ou de la signification du présent courrier, selon le mode
          d'envoi retenu et les règles applicables.
        </p>
        {proof && <p>{proof}</p>}
        {inspection && <p>{inspection}</p>}
        <p>
          Je vous prie d'agréer, Madame, Monsieur, l'expression de mes salutations
          distinguées.
        </p>
        <p>
          Signature :
          <br />
          <br />
          {tenantName}
        </p>
      </article>
    ),
    text: [
      `${tenantName}`,
      `${tenantAddress}`,
      "",
      `${landlordName}`,
      `${landlordAddress}`,
      "",
      `Fait à ${city}, le ${sendDate}`,
      "",
      `Mode d'envoi : ${mode}`,
      `Situation indiquée : ${situation}`,
      "",
      `Objet : congé du logement situé ${tenantAddress}`,
      "",
      "Madame, Monsieur,",
      "",
      `Par la présente, je vous informe de ma décision de donner congé du logement que j'occupe à l'adresse suivante : ${tenantAddress}.`,
      "",
      `D'après les informations renseignées dans ce modèle, ce congé est donné avec ${noticeInfo.wording}. Le préavis court à compter de la réception, de la remise ou de la signification du présent courrier, selon le mode d'envoi retenu et les règles applicables.`,
      proof,
      inspection,
      "",
      "Je vous prie d'agréer, Madame, Monsieur, l'expression de mes salutations distinguées.",
      "",
      "Signature :",
      "",
      tenantName,
    ]
      .filter(Boolean)
      .join("\n"),
  };
}

export default function LettrePreavisLocation() {
  const [form, setForm] = useState(initialForm);
  const [generated, setGenerated] = useState(false);
  const [copyStatus, setCopyStatus] = useState("");
  const startedRef = useRef(false);

  const noticeInfo = useMemo(() => getNoticeInfo(form), [form]);
  const letter = useMemo(() => buildLetter(form, noticeInfo), [form, noticeInfo]);

  const onChange = (field, value) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent("preavis_form_started", { field });
    }
    setCopyStatus("");
    setForm((current) => ({ ...current, [field]: value }));
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setGenerated(true);
    trackEvent("preavis_letter_generated", {
      housing_type: form.housingType,
      situation: form.noticeSituation,
      notice_label: noticeInfo.label,
    });
  };

  const copyLetter = async () => {
    try {
      await navigator.clipboard.writeText(letter.text);
      setCopyStatus("Lettre copiée.");
      trackEvent("preavis_letter_copied", { notice_label: noticeInfo.label });
    } catch {
      setCopyStatus("Copie impossible depuis ce navigateur. Vous pouvez sélectionner le texte manuellement.");
    }
  };

  const printLetter = () => {
    trackEvent("preavis_letter_printed", { notice_label: noticeInfo.label });
    window.print();
  };

  const resetForm = () => {
    setForm(initialForm);
    setGenerated(false);
    setCopyStatus("");
    startedRef.current = false;
  };

  const softwareJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Générateur de lettre de préavis location",
    applicationCategory: "LegalService",
    applicationSubCategory: "Générateur de lettre de préavis location",
    operatingSystem: "Web",
    url: `https://etatdulieu.com${path}`,
    inLanguage: "fr-FR",
    dateModified: DEMENAGEMENT_DATE_MODIFIED,
    featureList: [
      "Lettre de préavis location vide",
      "Lettre de préavis location meublée",
      "Préavis réduit zone tendue",
      "Préavis réduit mutation, perte d'emploi, santé, RSA, AAH ou logement social",
      "Copie et impression de la lettre",
    ],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "EUR",
    },
  };

  return (
    <DemenagementLayout
      title="Lettre de préavis location : générateur gratuit à imprimer"
      description="Générez gratuitement une lettre de préavis de location à copier ou à imprimer, avec rappels pour préavis standard ou réduit et sources officielles."
      path={path}
      breadcrumbs={breadcrumbs}
      jsonLd={[softwareJsonLd]}
      label="Outil gratuit"
      printMode="document"
    >
      <Breadcrumbs items={breadcrumbs} />

      <header className="move-tool-header">
        <h1>Générateur de lettre de préavis location</h1>
        <p>
          Remplissez les informations utiles, générez votre courrier, puis
          vérifiez les règles applicables à votre situation sur les sources
          officielles avant envoi. L'outil est accessible sans compte.
        </p>
      </header>

      <section className="move-note no-print" aria-labelledby="preavis-cases-title">
        <h2 id="preavis-cases-title">Le générateur couvre les cas les plus recherchés</h2>
        <ul className="move-pill-list">
          <li>location vide</li>
          <li>location meublée</li>
          <li>zone tendue</li>
          <li>mutation professionnelle</li>
          <li>perte ou nouvel emploi</li>
          <li>raison de santé</li>
          <li>RSA / AAH</li>
          <li>logement social</li>
          <li>remise en main propre</li>
          <li>lettre recommandée</li>
        </ul>
        <p>
          Ces rappels restent généraux : pour une lettre de préavis d'un mois,
          vérifiez toujours le justificatif demandé et la date de départ du délai
          avant d'envoyer le courrier.
        </p>
      </section>

      <div className="move-tool-grid">
        <form className="move-form-panel" onSubmit={onSubmit}>
          <div className="move-form-grid">
            <div className="move-field">
              <label htmlFor="housingType">Type de logement</label>
              <select
                id="housingType"
                value={form.housingType}
                onChange={(event) => onChange("housingType", event.target.value)}
              >
                <option value="vide">Location vide</option>
                <option value="meublé">Location meublée</option>
              </select>
            </div>

            <div className="move-field">
              <label htmlFor="noticeSituation">Situation de préavis</label>
              <select
                id="noticeSituation"
                value={form.noticeSituation}
                onChange={(event) => onChange("noticeSituation", event.target.value)}
              >
                {Object.entries(situationLabels).map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="move-field">
              <label htmlFor="tenantName">Nom du locataire</label>
              <input
                id="tenantName"
                value={form.tenantName}
                onChange={(event) => onChange("tenantName", event.target.value)}
                placeholder="Ex. Camille Durand"
              />
            </div>

            <div className="move-field">
              <label htmlFor="tenantAddress">Adresse du logement quitté</label>
              <input
                id="tenantAddress"
                value={form.tenantAddress}
                onChange={(event) => onChange("tenantAddress", event.target.value)}
                placeholder="Ex. 12 rue des Lilas, 75000 Paris"
              />
            </div>

            <div className="move-field">
              <label htmlFor="landlordName">Nom du bailleur/agence</label>
              <input
                id="landlordName"
                value={form.landlordName}
                onChange={(event) => onChange("landlordName", event.target.value)}
                placeholder="Ex. Agence Martin"
              />
            </div>

            <div className="move-field">
              <label htmlFor="landlordAddress">Adresse du bailleur/agence</label>
              <input
                id="landlordAddress"
                value={form.landlordAddress}
                onChange={(event) => onChange("landlordAddress", event.target.value)}
                placeholder="Ex. 4 avenue Victor Hugo, 75000 Paris"
              />
            </div>

            <div className="move-field">
              <label htmlFor="city">Ville de rédaction</label>
              <input
                id="city"
                value={form.city}
                onChange={(event) => onChange("city", event.target.value)}
                placeholder="Ex. Lyon"
              />
            </div>

            <div className="move-field">
              <label htmlFor="sendDate">Date d'envoi</label>
              <input
                id="sendDate"
                type="date"
                value={form.sendDate}
                onChange={(event) => onChange("sendDate", event.target.value)}
              />
            </div>

            <div className="move-field move-field-full">
              <label htmlFor="sendMode">Mode d'envoi</label>
              <select
                id="sendMode"
                value={form.sendMode}
                onChange={(event) => onChange("sendMode", event.target.value)}
              >
                <option value="lettre_recommandee">Lettre recommandée avec avis de réception</option>
                <option value="remise_main_propre">Remise en main propre contre récépissé</option>
                <option value="commissaire_justice">Acte de commissaire de justice</option>
              </select>
            </div>

            <label className="move-checkbox-row move-field-full">
              <input
                type="checkbox"
                checked={form.requestExitInspection}
                onChange={(event) => onChange("requestExitInspection", event.target.checked)}
              />
              Demander une proposition de dates pour l'état des lieux de sortie
            </label>
          </div>

          {noticeInfo.needsProof && (
            <aside className="move-warning" aria-label="À vérifier">
              <h2>À vérifier</h2>
              <p>
                Vous avez indiqué une situation pouvant relever d'un préavis
                réduit. Vérifiez votre cas exact et joignez les justificatifs
                nécessaires avant l'envoi.
              </p>
            </aside>
          )}

          <div className="move-actions">
            <button className="move-btn move-btn-primary" type="submit">
              Générer la lettre
            </button>
            <button className="move-btn move-btn-muted" type="button" onClick={resetForm}>
              Réinitialiser
            </button>
          </div>
        </form>

        <section className="move-letter-preview" aria-label="Aperçu de la lettre">
          <h2>Aperçu imprimable</h2>
          {generated ? (
            <>
              {letter.html}
              <div className="move-actions no-print">
                <button className="move-btn move-btn-primary" type="button" onClick={copyLetter}>
                  Copier la lettre
                </button>
                <button className="move-btn move-btn-secondary" type="button" onClick={printLetter}>
                  Imprimer la lettre
                </button>
              </div>
              {copyStatus && <p className="no-print">{copyStatus}</p>}
            </>
          ) : (
            <div className="move-letter-placeholder">
              <p>La lettre apparaîtra ici après génération.</p>
            </div>
          )}
        </section>
      </div>

      <aside className="move-source-note no-print">
        <h2>Important</h2>
        <p>
          Cet outil aide à rédiger un courrier. Il ne fournit pas de conseil
          juridique et ne remplace pas la vérification des règles applicables à
          votre bail, votre logement et votre situation personnelle.
        </p>
      </aside>

      <section className="move-note no-print" aria-labelledby="future-address-preavis-title">
        <h2 id="future-address-preavis-title">Après votre préavis, sécurisez le choix du prochain logement</h2>
        <p>
          Beaucoup d'utilisateurs arrivent ici au moment où ils changent d'adresse.
          Avant de signer le nouveau bail ou de confirmer une visite, une fiche
          Dictimmo peut compléter votre décision avec les prix du secteur, les
          risques naturels, l'environnement, la météo, les services et les données
          de la commune.
        </p>
      </section>

      {generated && (
        <DictimmoCta
          title="Avant d'envoyer votre préavis, vérifiez votre future adresse"
          text="Prix du secteur, risques naturels, environnement, météo, commune : obtenez une fiche gratuite en quelques secondes."
          buttonLabel="Vérifier ma future adresse"
          eventName="dictimmo_cta_clicked_from_preavis"
        />
      )}

      <section className="move-note no-print">
        <h2>Besoin de comprendre le délai avant de générer ?</h2>
        <p>
          Consultez le guide <Link to="/guides/preavis-location-1-mois-ou-3-mois">préavis location : 1 mois ou 3 mois</Link>,
          puis revenez ici pour produire votre courrier.
        </p>
      </section>

      <InternalLinks currentPath={path} />
      <SourceList sources={sources} />
    </DemenagementLayout>
  );
}
